import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import {
  DiscordIcon,
  ExcalLogo,
  GithubIcon,
  XBrandIcon,
  exportToPlus,
  share,
  usersIcon,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { THEME } from "@excalidraw/common";
import { t } from "@excalidraw/excalidraw/i18n";
import { type Dispatch, type SetStateAction } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { Theme } from "@excalidraw/element/types";

import { exportToExcalidrawPlus } from "../components/ExportToExcalidrawPlus";

import type { CollabAPI } from "../collab/Collab";

type ShareDialogState =
  | { isOpen: false }
  | { isOpen: true; type: "share" | "collaborationOnly" };

type GetCommandPaletteItemsParams = {
  collabAPI: CollabAPI | null;
  setShareDialogState: (state: ShareDialogState) => void;
  setAppTheme: Dispatch<SetStateAction<Theme | "system">>;
  editorTheme: Theme;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  getPwaEvent: () => BeforeInstallPromptEvent | null;
  setPwaEvent: (event: BeforeInstallPromptEvent | null) => void;
  isExcalidrawPlusSignedUser: boolean;
};

export const getCommandPaletteItems = ({
  collabAPI,
  setShareDialogState,
  setAppTheme,
  editorTheme,
  excalidrawAPI,
  getPwaEvent,
  setPwaEvent,
  isExcalidrawPlusSignedUser,
}: GetCommandPaletteItemsParams) => {
  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return [
    {
      label: t("labels.liveCollaboration"),
      category: DEFAULT_CATEGORIES.app,
      keywords: ["team", "multiplayer", "share", "public", "session", "invite"],
      icon: usersIcon,
      perform: () => {
        setShareDialogState({
          isOpen: true,
          type: "collaborationOnly",
        });
      },
    },
    {
      label: t("roomDialog.button_stopSession"),
      category: DEFAULT_CATEGORIES.app,
      predicate: () => !!collabAPI?.isCollaborating(),
      keywords: [
        "stop",
        "session",
        "end",
        "leave",
        "close",
        "exit",
        "collaboration",
      ],
      perform: () => {
        if (collabAPI) {
          collabAPI.stopCollaboration();
          if (!collabAPI.isCollaborating()) {
            setShareDialogState({ isOpen: false });
          }
        }
      },
    },
    {
      label: t("labels.share"),
      category: DEFAULT_CATEGORIES.app,
      predicate: true,
      icon: share,
      keywords: [
        "link",
        "shareable",
        "readonly",
        "export",
        "publish",
        "snapshot",
        "url",
        "collaborate",
        "invite",
      ],
      perform: async () => {
        setShareDialogState({ isOpen: true, type: "share" });
      },
    },
    {
      label: "GitHub",
      icon: GithubIcon,
      category: DEFAULT_CATEGORIES.links,
      predicate: true,
      keywords: [
        "issues",
        "bugs",
        "requests",
        "report",
        "features",
        "social",
        "community",
      ],
      perform: () => {
        window.open(
          "https://github.com/excalidraw/excalidraw",
          "_blank",
          "noopener noreferrer",
        );
      },
    },
    {
      label: t("labels.followUs"),
      icon: XBrandIcon,
      category: DEFAULT_CATEGORIES.links,
      predicate: true,
      keywords: ["twitter", "contact", "social", "community"],
      perform: () => {
        window.open(
          "https://x.com/excalidraw",
          "_blank",
          "noopener noreferrer",
        );
      },
    },
    {
      label: t("labels.discordChat"),
      category: DEFAULT_CATEGORIES.links,
      predicate: true,
      icon: DiscordIcon,
      keywords: [
        "chat",
        "talk",
        "contact",
        "bugs",
        "requests",
        "report",
        "feedback",
        "suggestions",
        "social",
        "community",
      ],
      perform: () => {
        window.open(
          "https://discord.gg/UexuTaE",
          "_blank",
          "noopener noreferrer",
        );
      },
    },
    {
      label: "YouTube",
      icon: youtubeIcon,
      category: DEFAULT_CATEGORIES.links,
      predicate: true,
      keywords: ["features", "tutorials", "howto", "help", "community"],
      perform: () => {
        window.open(
          "https://youtube.com/@excalidraw",
          "_blank",
          "noopener noreferrer",
        );
      },
    },
    ...(isExcalidrawPlusSignedUser
      ? [
          {
            ...ExcalidrawPlusAppCommand,
            label: "Sign in / Go to Excalidraw+",
          },
        ]
      : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),
    {
      label: t("overwriteConfirm.action.excalidrawPlus.button"),
      category: DEFAULT_CATEGORIES.export,
      icon: exportToPlus,
      predicate: true,
      keywords: ["plus", "export", "save", "backup"],
      perform: () => {
        if (excalidrawAPI) {
          exportToExcalidrawPlus(
            excalidrawAPI.getSceneElements(),
            excalidrawAPI.getAppState(),
            excalidrawAPI.getFiles(),
            excalidrawAPI.getName(),
          );
        }
      },
    },
    {
      ...CommandPalette.defaultItems.toggleTheme,
      perform: () => {
        setAppTheme(editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK);
      },
    },
    {
      label: t("labels.installPWA"),
      category: DEFAULT_CATEGORIES.app,
      predicate: () => !!getPwaEvent(),
      perform: () => {
        const pwaEvent = getPwaEvent();
        if (pwaEvent) {
          pwaEvent.prompt();
          pwaEvent.userChoice.then(() => {
            // event cannot be reused, but we'll hopefully
            // grab new one as the event should be fired again
            setPwaEvent(null);
          });
        }
      },
    },
  ];
};
